import { ArrowLeft, Moon, Sun, Palette, Sparkles } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { goBackOrHome } from '../../lib/navigation';
import { useThemeStore } from '../../lib/themeStore';

export function ThemeDemo() {
  const navigate = useNavigate();
  const { theme, toggleTheme } = useThemeStore();

  const demoComponents = [
    {
      title: 'Cards & Content',
      component: (
        <div className="card mb-3">
          <div className="card-header">
            <h6 className="mb-0">Sample Card Header</h6>
          </div>
          <div className="card-body">
            <h5 className="card-title">Card Title</h5>
            <p className="card-text text-muted">
              This is a sample card to demonstrate the dark theme. 
              All text and backgrounds adapt to the selected theme.
            </p>
            <button className="btn btn-primary me-2">Primary Button</button>
            <button className="btn btn-secondary">Secondary Button</button>
          </div>
        </div>
      )
    },
    {
      title: 'Form Elements',
      component: (
        <div className="card mb-3">
          <div className="card-body">
            <form>
              <div className="mb-3">
                <label htmlFor="email" className="form-label">Email</label>
                <input type="email" className="form-control" id="email" placeholder="example@hockey.com" />
              </div>
              <div className="mb-3">
                <label htmlFor="league" className="form-label">League</label>
                <select className="form-select" id="league">
                  <option>Choose a league...</option>
                  <option>NHL Fantasy Pro</option>
                  <option>Dynasty Champions</option>
                </select>
              </div>
              <div className="form-check form-switch">
                <input className="form-check-input" type="checkbox" id="notifications" />
                <label className="form-check-label" htmlFor="notifications">
                  Enable notifications
                </label>
              </div>
            </form>
          </div>
        </div>
      )
    },
    {
      title: 'Navigation & Alerts',
      component: (
        <div>
          <ul className="nav nav-pills mb-3">
            <li className="nav-item">
              <button className="nav-link active">Active Tab</button>
            </li>
            <li className="nav-item">
              <button className="nav-link">Regular Tab</button>
            </li>
            <li className="nav-item">
              <button className="nav-link">Another Tab</button>
            </li>
          </ul>
          
          <div className="alert alert-info mb-3">
            <strong>Info:</strong> This is an info alert demonstrating theme adaptation.
          </div>
          
          <div className="list-group">
            <div className="list-group-item">
              <strong>Player 1:</strong> Connor McDavid - 50 points
            </div>
            <div className="list-group-item">
              <strong>Player 2:</strong> Sidney Crosby - 45 points
            </div>
            <div className="list-group-item">
              <strong>Player 3:</strong> Nathan MacKinnon - 48 points
            </div>
          </div>
        </div>
      )
    },
    {
      title: 'Tables & Progress',
      component: (
        <div>
          <table className="table table-striped mb-3">
            <thead>
              <tr>
                <th>Team</th>
                <th>Points</th>
                <th>Wins</th>
              </tr>
            </thead>
            <tbody>
              <tr>
                <td>Team Alpha</td>
                <td>150</td>
                <td>12</td>
              </tr>
              <tr>
                <td>Team Beta</td>
                <td>145</td>
                <td>11</td>
              </tr>
              <tr>
                <td>Team Gamma</td>
                <td>140</td>
                <td>10</td>
              </tr>
            </tbody>
          </table>
          
          <div className="mb-3">
            <label className="form-label">Team Performance</label>
            <div className="progress">
              <div className="progress-bar bg-success" style={{ width: '75%' }}>75%</div>
            </div>
          </div>
          
          <div className="d-flex justify-content-center">
            <div className="spinner-border" role="status">
              <span className="visually-hidden">Loading...</span>
            </div>
          </div>
        </div>
      )
    }
  ];

  return (
    <div className="card shadow-lg border-0">
      <div className="card-body p-5">
        <button
          className="btn btn-link text-primary mb-4 p-0 d-flex align-items-center"
          onClick={() => goBackOrHome(navigate)}
        >
          <ArrowLeft size={20} className="me-2" />
          Back
        </button>

        <div className="text-center mb-5">
          <Palette className="h-12 w-12 text-primary mb-2" />
          <h2 className="card-title text-center mb-2">Theme Demo</h2>
          <p className="text-muted">
            Explore how all components adapt to dark and light themes
          </p>
        </div>

        {/* Theme Toggle Section */}
        <div className="card mb-5 border-2" style={{ borderColor: 'var(--bs-primary)' }}>
          <div className="card-body text-center">
            <div className="row align-items-center">
              <div className="col-md-4">
                <div className="d-flex align-items-center justify-content-center">
                  <Sun className="me-2" size={24} />
                  <span className="fw-bold">Light Mode</span>
                </div>
              </div>
              <div className="col-md-4">
                <button
                  className="btn btn-primary btn-lg d-flex align-items-center mx-auto"
                  onClick={toggleTheme}
                >
                  {theme === 'dark' ? <Sun className="me-2" /> : <Moon className="me-2" />}
                  Switch to {theme === 'dark' ? 'Light' : 'Dark'} Mode
                  <Sparkles className="ms-2" size={18} />
                </button>
              </div>
              <div className="col-md-4">
                <div className="d-flex align-items-center justify-content-center">
                  <Moon className="me-2" size={24} />
                  <span className="fw-bold">Dark Mode</span>
                </div>
              </div>
            </div>
            <div className="mt-3">
              <small className="text-muted">
                Current theme: <strong className="text-primary">{theme === 'dark' ? 'Dark' : 'Light'}</strong>
              </small>
            </div>
          </div>
        </div>

        {/* Demo Components */}
        <div className="row">
          {demoComponents.map((demo, index) => (
            <div key={index} className="col-md-6 mb-4">
              <h5 className="mb-3 d-flex align-items-center">
                <span className="badge bg-primary me-2">{index + 1}</span>
                {demo.title}
              </h5>
              {demo.component}
            </div>
          ))}
        </div>

        {/* Features List */}
        <div className="card mt-5">
          <div className="card-header">
            <h5 className="mb-0">Dark Theme Features</h5>
          </div>
          <div className="card-body">
            <div className="row">
              <div className="col-md-6">
                <ul className="list-unstyled">
                  <li className="mb-2">✅ Automatic theme persistence</li>
                  <li className="mb-2">✅ Smooth transitions</li>
                  <li className="mb-2">✅ All Bootstrap components</li>
                  <li className="mb-2">✅ Form elements styling</li>
                </ul>
              </div>
              <div className="col-md-6">
                <ul className="list-unstyled">
                  <li className="mb-2">✅ Navigation components</li>
                  <li className="mb-2">✅ Tables and progress bars</li>
                  <li className="mb-2">✅ Cards and alerts</li>
                  <li className="mb-2">✅ Landing page excluded</li>
                </ul>
              </div>
            </div>
          </div>
        </div>

        <div className="text-center mt-4">
          <small className="text-muted">
            The landing page remains in light theme while all other pages respect your theme preference.
          </small>
        </div>
      </div>
    </div>
  );
}
